/**
 * Auth Feature — Public API
 *
 * Arquitectura:
 *  - Supabase Auth: login, registro, sesión JWT
 *  - Backend NestJS: perfil de usuario, datos de negocio
 *  - El JWT de Supabase se envía al backend como Bearer token
 *
 * Import auth functionality from this barrel export only.
 * Internal feature files should NOT be imported directly from outside.
 *
 * @example
 * ```tsx
 * import {
 *   useAuth,
 *   useAuthStore,
 *   useRegistrationWizard,
 *   LoginForm,
 *   RegisterForm,
 *   AuthProvider,
 *   authService,
 *   loginSchema,
 *   type AuthUser,
 * } from '@/features/auth'
 * ```
 */

// Components
export { AuthProvider } from './components/auth-provider'
export { LoginForm } from './components/login-form'
export { RegisterForm } from './components/register-form'

// Hooks
export { useAuth } from './hooks/use-auth'
export { useAuthListener } from './hooks/use-auth-listener'
export { useRegistrationWizard } from './hooks/use-registration-wizard'

// Store - useAuthStore provides both setUser and setAuth (alias for backwards compatibility)
export { useAuthStore } from './stores/auth-store'
export {
	useRegistrationStore,
	type RegistrationData,
	type RegistrationStep,
} from './stores/registration-store'

// Services
export { authService, type BackendUser } from './services/auth-service'

// Schemas
export {
	loginSchema,
	registerSchema,
	resetPasswordSchema,
	updatePasswordSchema,
	type LoginFormData,
	type RegisterFormData,
	type ResetPasswordFormData,
	type UpdatePasswordFormData,
} from './schemas'

// Types
export type {
	AuthSession,
	AuthState,
	AuthUser,
	LoginCredentials,
	RegisterCredentials,
	ResetPasswordRequest,
	UpdatePasswordRequest,
	UserProfile,
	UserRole,
} from './types'
